/**
 * apiClient.js — Cliente HTTP centralizado com gestão automática de JWT.
 * 
 * Todas as chamadas API devem usar este módulo ao invés de fetch() direto.
 * Ele automaticamente:
 * - Anexa o token JWT no header Authorization
 * - Tenta refresh do token quando recebe 401
 * - Redireciona para login quando o refresh falha
 */
import { marcarSessaoExpirada, pedirNovaEntrada } from './components/casca/sessaoExpirada';

const TOKEN_KEY = 'erp_access_token';
const REFRESH_KEY = 'erp_refresh_token';
const USER_KEY = 'erp_usuario';
// S2: o JWT de master de plataforma (Bunker, rota /admin) tem chave PROPRIA.
// Dividir chave com o app de tenant vazava credencial de plataforma para dentro
// da sessao do cliente — e ela sobrevivia ao logout.
const BUNKER_TOKEN_KEY = 'bunker_access_token';
// Chaves gravadas antes do S2; removidas em todo clearAuth para limpar quem ja
// carrega o token vazado no navegador.
const LEGACY_BUNKER_KEYS = ['bunker_token'];

// S9: a rota do Bunker, em UM lugar só. O `routes/index.jsx` monta a rota do
// AdminMaster com este mesmo valor, e o guarda em __guards__ prova que nenhuma
// outra rota nasce embaixo dele.
export const ROTA_BUNKER = '/admin';

/**
 * Estamos rodando dentro do painel de master de plataforma?
 *
 * S9: compara por SEGMENTO de caminho, não por prefixo solto. Com
 * `startsWith('/admin')`, uma rota futura como `/administrativo` cairia no
 * escopo de master em silêncio e passaria a ler e gravar o token da
 * plataforma na tela de um escritório. A propriedade valia por acidente
 * (nenhuma rota assim existe hoje); agora vale por construção.
 */
export function isBunkerScope() {
  if (typeof window === 'undefined') return false;
  const caminho = window.location.pathname;
  return caminho === ROTA_BUNKER || caminho.startsWith(`${ROTA_BUNKER}/`);
}

function accessTokenKey() {
  return isBunkerScope() ? BUNKER_TOKEN_KEY : TOKEN_KEY;
}
// RULE 6: Bloqueado a fallback inseguro para localhost em producao. Tem que vir do .env!
const apiUrl = process.env.REACT_APP_API_URL;
if (!apiUrl) {
  console.error("🔥 ERRO FATAL (RULE 6 / 24): REACT_APP_API_URL nao foi configurada no .env!");
}
export const BASE_URL = (apiUrl || "").replace(/\/$/, "");

// ==========================================
// GESTÃO DE TOKENS
// ==========================================
export function getAccessToken() {
  return localStorage.getItem(accessTokenKey());
}

export function getRefreshToken() {
  // O Bunker nao tem refresh token: sessao de master expira e exige login de novo.
  if (isBunkerScope()) return null;
  return localStorage.getItem(REFRESH_KEY);
}

export function setTokens(accessToken, refreshToken) {
  if (accessToken) localStorage.setItem(accessTokenKey(), accessToken);
  if (refreshToken && !isBunkerScope()) localStorage.setItem(REFRESH_KEY, refreshToken);
}

/**
 * Limpa SEMPRE os dois escopos. Sair de um lado nunca pode deixar credencial
 * do outro para tras — e o que causava o vazamento do S2.
 */
export function clearAuth() {
  // Item 4B-1: uma renovação em andamento não pode regravar tokens depois
  // de um logout — a resposta dela compara este número antes de gravar.
  geracaoDoCliente += 1;
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_KEY);
  localStorage.removeItem(USER_KEY);
  localStorage.removeItem(BUNKER_TOKEN_KEY);
  localStorage.removeItem('bunker_email');
  LEGACY_BUNKER_KEYS.forEach((k) => localStorage.removeItem(k));
}

export function getStoredUser() {
  const data = localStorage.getItem(USER_KEY);
  if (!data || data === 'undefined' || data === 'null') return null;
  try {
    return JSON.parse(data);
  } catch (e) {
    return null;
  }
}

export function setStoredUser(user) {
  localStorage.setItem(USER_KEY, JSON.stringify(user));
}


// ==========================================
// FETCH COM JWT AUTOMÁTICO
// ==========================================
let isRefreshing = false;
let refreshPromise = null;
let geracaoDoCliente = 0;

// A sessão ainda é a mesma que pediu a renovação? Logout, novo login ou
// troca em outra aba mudam o contador ou o refresh guardado — e então a
// resposta é de outra sessão e não pode ter efeito NENHUM (nem limpar).
function mesmaSessao(geracao, refreshToken) {
  return geracao === geracaoDoCliente && getRefreshToken() === refreshToken;
}

function erroTransitorio() {
  const erro = new Error('Servidor indisponível. Tente de novo em instantes.');
  erro.transitorio = true;
  return erro;
}

/**
 * A renovação foi recusada: a sessão acabou. Com a casca aberta, o diálogo pede a
 * senha sobre a tela e devolve o token novo (o pedido original é refeito). Sem
 * ele, o caminho antigo: marca o motivo, limpa e recarrega para o login.
 */
function sessaoExpirou() {
  const usuario = getStoredUser();
  clearAuth();
  const espera = pedirNovaEntrada(usuario);
  if (!espera) {
    marcarSessaoExpirada();
    window.location.reload();
    throw new Error('Sessão expirada');
  }
  // A tela continua de pé: o usuário guardado volta (sem token nenhum) até a nova entrada.
  setStoredUser(usuario);
  return espera;
}

async function refreshAccessToken() {
  const geracao = geracaoDoCliente;
  const refreshToken = getRefreshToken();
  if (!refreshToken) {
    return sessaoExpirou();
  }

  let res;
  try {
    res = await fetch(`${BASE_URL}/api/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: refreshToken })
    });
  } catch (_) {
    // Rede fora: não é sessão inválida. A sessão fica; o pedido não se repete.
    throw erroTransitorio();
  }

  if (!mesmaSessao(geracao, refreshToken)) {
    throw new Error('Sessão encerrada');
  }
  // Indisponibilidade (429/5xx) não é recusa da credencial: não desloga.
  if (res.status === 429 || res.status >= 500) {
    throw erroTransitorio();
  }
  if (!res.ok) {
    return sessaoExpirou();
  }

  const corpo = await res.json();
  // A leitura do corpo também leva tempo: confere de novo antes de gravar.
  if (!mesmaSessao(geracao, refreshToken)) {
    throw new Error('Sessão encerrada');
  }
  // Envelope R7 ({success, data}) ou o formato antigo, plano.
  const data = corpo?.data ?? corpo;
  if (!data?.access_token) {
    return sessaoExpirou();
  }
  setTokens(data.access_token, data.refresh_token);
  if (data.usuario) setStoredUser(data.usuario);
  return data.access_token;
}

/**
 * Wrapper de fetch() que injeta automaticamente o token JWT.
 * Uso: `const res = await apiFetch('/api/crm/listar');`
 */
export async function apiFetch(url, options = {}) {
  const geracaoDoPedido = geracaoDoCliente;
  const token = getAccessToken();
  
  const headers = {
    ...options.headers,
  };

  // Não adicionar Content-Type se for FormData (upload de arquivos)
  if (!(options.body instanceof FormData)) {
    headers['Content-Type'] = headers['Content-Type'] || 'application/json';
  }

  // Header explícito vence o token guardado: o MFA do master manda o token
  // de desafio por aqui, e uma sessão antiga do Bunker no navegador o
  // substituía em silêncio (item 3, 29/09/2026).
  const temAuthorizationExplicito = Object.keys(headers)
    .some((nome) => nome.toLowerCase() === 'authorization');
  if (token && !temAuthorizationExplicito) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const finalUrl = url.startsWith('/api') ? `${BASE_URL}${url}` : url;
  let response = await fetch(finalUrl, { ...options, headers });

  // Se receber 401, tentar refresh do token — só se a requisição usou o token
  // guardado. Um 401 do token explícito (código de MFA errado) não é sessão
  // expirada, e o refresh apagaria a sessão e recarregaria a página.
  // Recusa com motivo (X-Motivo: senha ou código errado, tentativa vencida)
  // não é sessão expirada: renovar e repetir não faz sentido (item 4B).
  const temMotivo = Boolean(response.headers?.get?.('X-Motivo'));
  // O pedido saiu com uma sessão que já não é a atual (logout, novo login,
  // troca em outra aba): o 401 é dela; não renova nem repete com a nova.
  const sessaoMudou = geracaoDoPedido !== geracaoDoCliente || getAccessToken() !== token;
  if (response.status === 401 && token && !temAuthorizationExplicito && !temMotivo && !sessaoMudou) {
    try {
      // Evitar múltiplos refreshes simultâneos
      if (!isRefreshing) {
        isRefreshing = true;
        refreshPromise = refreshAccessToken();
      }
      
      const newToken = await refreshPromise;
      isRefreshing = false;
      refreshPromise = null;

      // Refazer a requisição original com o novo token
      headers['Authorization'] = `Bearer ${newToken}`;
      response = await fetch(finalUrl, { ...options, headers });
    } catch (err) {
      isRefreshing = false;
      refreshPromise = null;
      // Sessão expirada — refresh já limpa e recarrega a página
      throw err;
    }
  }

  return response;
}

export default apiFetch;
